import { booleanAttribute, ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { DEMO_MODE } from './demo-mode';

/** `bar` — pasek na całą szerokość treści; `card` — kafel wewnątrz karty logowania. */
export type DemoNoticeVariant = 'bar' | 'card';

@Component({
  selector: 'app-demo-notice',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe],
  // Pasek ma zostać widoczny także po przewinięciu treści — `main` w powłoce jest kontenerem scrolla.
  host: { '[class.demo-notice-host--sticky]': "variant() === 'bar'" },
  template: `
    @if (demoMode) {
      @if (variant() === 'bar') {
        <div class="demo-notice demo-notice--bar" role="note" [attr.aria-label]="'demo.regionLabel' | transloco">
          <span class="demo-notice__badge">{{ 'demo.badge' | transloco }}</span>
          <p class="demo-notice__text">{{ 'demo.bar' | transloco }}</p>
        </div>
      } @else {
        <div class="demo-notice demo-notice--card" role="note" [attr.aria-label]="'demo.regionLabel' | transloco">
          <p class="demo-notice__title">
            <span class="demo-notice__badge">{{ 'demo.badge' | transloco }}</span>
            {{ 'demo.title' | transloco }}
          </p>
          <ul class="demo-notice__list">
            <li>{{ 'demo.data' | transloco }}</li>
            <li>{{ 'demo.noSave' | transloco }}</li>
            @if (loginHint()) {
              <li>{{ 'demo.login' | transloco }}</li>
            }
          </ul>
        </div>
      }
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }
      :host(.demo-notice-host--sticky) {
        position: sticky;
        top: 0;
        z-index: 5;
      }
      .demo-notice {
        color: var(--warning-text);
      }
      /* Przypięty pasek musi być nieprzezroczysty — w motywie ciemnym
         --warning-soft jest półprzezroczyste, więc kładziemy je na tle aplikacji. */
      .demo-notice--bar {
        background: linear-gradient(var(--warning-soft), var(--warning-soft)) var(--app-bg);
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.625rem 1rem;
        border-bottom: var(--border-w) solid var(--warning);
      }
      .demo-notice--card {
        background: var(--warning-soft);
        margin-bottom: 1.25rem;
        padding: 0.875rem 1rem;
        border: var(--border-w) solid var(--warning);
        border-radius: var(--r-md);
        /* Karty auth bywają wyśrodkowane (np. reset hasła) — lista musi zostać do lewej. */
        text-align: left;
      }
      /* Plakietka dziedziczy --warning-text zamiast pary --warning/--on-warning:
         ta ostatnia daje w motywie jasnym 3.8:1, czyli poniżej progu WCAG AA. */
      .demo-notice__badge {
        flex: 0 0 auto;
        padding: 0.125rem 0.5rem;
        border: var(--border-w) solid currentColor;
        border-radius: var(--r-pill);
        color: inherit;
        font-size: 0.6875rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
      .demo-notice__text {
        margin: 0;
        font-size: 0.875rem;
        font-weight: 500;
      }
      .demo-notice__title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin: 0 0 0.5rem;
        font-weight: 700;
      }
      .demo-notice__list {
        margin: 0;
        padding-left: 1.125rem;
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        font-size: 0.875rem;
      }
    `,
  ],
})
export class DemoNotice {
  protected readonly demoMode = inject(DEMO_MODE);

  readonly variant = input<DemoNoticeVariant>('bar');

  /** Dodaje zdanie o pozornym logowaniu — tylko na ekranie logowania. */
  readonly loginHint = input(false, { transform: booleanAttribute });
}
